import { Store } from 'vuex';
import { CenterModel, ConfigLookAndFeelModel, ConfigModel, ErrorModel, resolveAppTheme, TranslationModel } from '../../../common/src';
import { setColor } from '@/shared/color-utils';
import { WebAppStoreState } from '@/store';
import { App } from 'vue';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import * as backendApi from '../shared/backend-api';
import i18n, { Language, loadEnglishLocale, LocaleMessage, LocaleMessages } from '@/i18n';
import { SESSION_STORAGE_CENTER_ID } from './user.service';
import { THEME_CONFIG } from '@/themeConfig';

declare global {
  interface Window {
    dataLayer: any;
  }
}

export default class ConfigurationService {
  constructor(public store: Store<WebAppStoreState>, public app: App<Element>) {}

  loadConfiguration(force: boolean, center?: number, reloadApp?: boolean, isCurrent: () => boolean = () => true): Promise<ConfigModel> {
    // get the center from the url if it is there
    const url = new URL(window.location.href);
    let centerParam =
      (url.pathname.includes('/join') || url.pathname.includes('/create') || url.pathname.includes('/generate-lead')) &&
      url.searchParams.get('center')
        ? Number(url.searchParams.get('center'))
        : center;

    if (!(this.store.state.user && this.store.state.user)) {
      const sessionCenterId = sessionStorage.getItem(SESSION_STORAGE_CENTER_ID);
      if (centerParam) {
        sessionStorage.setItem(SESSION_STORAGE_CENTER_ID, String(centerParam));
        if (Number(sessionCenterId) !== Number(centerParam) && reloadApp) {
          window.location.reload();
        }
      } else if (sessionCenterId) {
        centerParam = Number(sessionCenterId);
      }
    }

    let queryParam = '';
    if (centerParam && this.store.state.config?.configCenterId !== centerParam) {
      queryParam = `?centerId=${centerParam}`;
    }

    if (!force && !queryParam && this.store.state.config !== undefined) {
      return new Promise<ConfigModel>((resolve, reject) => {
        resolve(this.store.state.config!);
      });
    } else {
      // this.store.commit('configError', undefined);
      return new Promise<ConfigModel>((resolve, reject) => {
        backendApi
          .get<ConfigModel>(`config/webapp-config${queryParam}`)
          .then((response: ConfigModel) => {
            // Session adoption can be invalidated while this request is in flight. Check before
            // applying any configuration, including styles, translations and integrations.
            if (!isCurrent()) {
              resolve(response);
              return;
            }
            if (!checkLookAndFeel(response.lookAndFeel)) {
              throw new ErrorModel('general.incompleteConfig');
            }
            const theme = response.themecustomization.theme;
            if (!setCssVariables(response.lookAndFeel, theme)) {
              throw new ErrorModel('general.incorrectColors');
            }

            this.initGoogleAnalytics(response);

            this.initGoogleTagManager(response);

            this.initGoogleMaps(response);

            this.preloadImages(response);

            this.setFavicon(response);

            this.setCustomCss(response);

            this.setLanguages(response);

            this.store.commit('config', response);
            this.store.commit('configError', undefined);
            // CHECK CONFIG ?
            resolve(response);
          })
          .catch((error: ErrorModel) => {
            if (isCurrent()) {
              this.store.commit('config', undefined);
              this.store.commit('configError', error.errorI18NMessage);
            }
            reject(error);
          });
      });
    }

    // } else { // Not Used
    //  // already loaded.. we do nothing
    //  return new Promise<ConfigModel>(resolve => {
    //    resolve(this.store.state.config!);
    //  });
    //}
  }
  setLanguages(response: ConfigModel) {
    const availableLocales: Language[] = [];
    for (const key in response.translations) {
      const language = response.translations[key] as TranslationModel;
      if (language) {
        availableLocales.push(language);
        let messages = language as LocaleMessages;
        if (key === 'english') {
          messages = loadEnglishLocale();
          mergeMessageOverrides(messages, language as TranslationMessageOverrides);
          // Custom English translations should only apply when English is selected. Other
          // languages intentionally fall back to the bundled English catalog for missing keys.
          i18n.global.fallbackLocale = 'en';
        }
        i18n.global.setLocaleMessage(language.code, messages);
      }
    }

    // sort availableLocales by order
    availableLocales.sort((a, b) => {
      return a.order - b.order;
    });

    if (!availableLocales.length) {
      availableLocales.push({
        code: 'en',
        locale: 'en-US',
        longName: 'English',
        shortName: 'EN',
        numberFormat: 'en',
        dateFormat: 'en',
        dateFormatFull: 'en',
        distanceUnit: 'metric',
        order: 1,
        direction: 'ltr',
      });
    }

    this.store.commit('availableLocales', availableLocales);
  }
  preloadImages(data: ConfigModel) {
    preload(data.lookAndFeel.bookingBanner);
    preload(data.lookAndFeel.staffBookingBanner);
    preload(data.lookAndFeel.resourceBookingBanner);
    preload(data.lookAndFeel.dashboardBanner);
    preload(data.lookAndFeel.profileBanner);
    preload(data.lookAndFeel.paymentsBanner);
    preload(data.lookAndFeel.clipcardBanner);
    preload(data.lookAndFeel.courseBanner);
    preload(data.lookAndFeel.campsBanner);
    preload(data.lookAndFeel.siteLogo);
    preload(data.lookAndFeel.favicon);
    preload(data.themecustomization.assets?.authPageBackground);
  }

  setFavicon(data: ConfigModel) {
    if (data.lookAndFeel.favicon) {
      const link: HTMLLinkElement = document.querySelector("link[rel~='icon']")!;
      link.href = data.lookAndFeel.favicon;
    }
  }

  setCustomCss(data: ConfigModel) {
    if (data.lookAndFeel.customCSS) {
      const styleSheet = document.createElement('style');
      styleSheet.innerText = data.lookAndFeel.customCSS;
      document.head.appendChild(styleSheet);
    }
  }

  loadCentersForArea(query?: { includePrivilegeAccessCenters?: boolean; includeHomeCentersOnly?: boolean }): Promise<CenterModel[]> {
    return backendApi.get('config/load-centers-for-area', query);
  }

  loadCentersForScopeId(scopeId: number): Promise<CenterModel[]> {
    return backendApi.get('config/load-addon-additional-centers', { id: scopeId });
  }

  loadStaffCenters(roles: string[]): Promise<CenterModel[]> {
    return backendApi.get('config/load-staff-centers', { roles });
  }

  initGoogleAnalytics(data: ConfigModel) {
    function gtag() {
      window.dataLayer.push(arguments);
    }

    if (data.general.googleAnalyticsClientId || data.general.googleAnalyticsExerpId) {
      /* istanbul ignore next @preserve */
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      gtag('js', new Date());
      // @ts-ignore
      data.general.googleAnalyticsClientId && gtag('config', data.general.googleAnalyticsClientId);
      // @ts-ignore
      data.general.googleAnalyticsExerpId && gtag('config', data.general.googleAnalyticsExerpId);
    }
  }

  initGoogleTagManager(data: ConfigModel) {
    if (data.general.googleTagManagerClientId) {
      /* istanbul ignore next @preserve */
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = document.getElementsByTagName('script')[0];
      const j = document.createElement('script') as HTMLScriptElement;
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + data.general.googleTagManagerClientId;
      f.parentNode && f.parentNode.insertBefore(j, f);
    }
  }

  initGoogleMaps(data: ConfigModel) {
    if (data.general.googleMapAPIKey) {
      this.app.use(VueGoogleMaps, {
        load: {
          key: data.general.googleMapAPIKey, // 'AIzaSyAmlmN3Spuolpvy8xtSeeYK2KgH0166I90',
          libraries: 'places,geometry',
        },
      });
    } else {
      // we init the component anyway, otherwise Vue complains about missing Component declaration even though it is not used
      this.app.use(VueGoogleMaps, {
        load: {
          libraries: 'places,geometry',
        },
      });
    }
  }
}

function setCssVariables(lookAndFeelConfig: ConfigLookAndFeelModel, theme?: string): boolean {
  let result = true;
  if (lookAndFeelConfig.highlightColor) {
    result = result && setColor(lookAndFeelConfig.highlightColor, 'highlight');
  }
  if (lookAndFeelConfig.makeAwareColor) {
    result = result && setColor(lookAndFeelConfig.makeAwareColor, 'makeAwareColor');
  }
  if (lookAndFeelConfig.bannerSummaryBackgroundColor) {
    result = result && setColor(lookAndFeelConfig.bannerSummaryBackgroundColor, 'bannerSummaryBackgroundColor');
  }
  if (lookAndFeelConfig.bannerSummaryTextColor) {
    result = result && setColor(lookAndFeelConfig.bannerSummaryTextColor, 'bannerSummaryTextColor');
  }
  if (lookAndFeelConfig.bannerSummaryButtonBackgroundColor) {
    result = result && setColor(lookAndFeelConfig.bannerSummaryButtonBackgroundColor, 'bannerSummaryButtonBackgroundColor');
  }
  result = result && setColor(lookAndFeelConfig.mainColor!, 'primary');
  result = result && setColor(lookAndFeelConfig.secondaryColor!, 'secondary');

  // set theme specific colors from the config handled in the frontend
  // The backend normalises the Theme property, so an unknown *value* cannot reach here. This
  // guards the field being ABSENT: frontend and backend ship as separate images and `theme` is
  // new, so a newer frontend can read an older backend's config. THEME_CONFIG[undefined] would
  // throw into the .catch below, which commits an undefined errorI18NMessage — a permanent spinner.
  const themeConfig = THEME_CONFIG[resolveAppTheme(theme)];
  document.documentElement.style.setProperty('--color-appBg', themeConfig.appBackgroundColor);
  document.documentElement.style.setProperty('--color-headerBg', themeConfig.headerBackgroundColor);
  document.documentElement.style.setProperty('--font-sans', themeConfig.fontSans);
  document.documentElement.style.setProperty('--font-heading', themeConfig.fontHeading);
  return result;
}

function checkLookAndFeel(lookAndFeel: ConfigLookAndFeelModel): boolean {
  if (!lookAndFeel.mainColor) {
    return false;
  }
  if (!lookAndFeel.secondaryColor) {
    return false;
  }
  if (!lookAndFeel.siteLogo) {
    return false;
  }
  return true;
}

function preload(image: string | undefined) {
  if (image) {
    const img = new Image();
    img.src = image;
  }
}
interface TranslationMessageOverrides {
  [key: string]: LocaleMessage | undefined;
}

function mergeMessageOverrides(messages: LocaleMessages, overrides: TranslationMessageOverrides): void {
  for (const key in overrides) {
    const override = overrides[key];
    const message = messages[key];

    if (isLocaleMessageGroup(override)) {
      if (isLocaleMessageGroup(message)) {
        mergeMessageOverrides(message, override);
      } else {
        messages[key] = cloneLocaleMessageGroup(override);
      }
    } else if (typeof override === 'string') {
      messages[key] = override;
    }
  }
}

function cloneLocaleMessageGroup(group: LocaleMessages): LocaleMessages {
  const clone: LocaleMessages = {};
  mergeMessageOverrides(clone, group);
  return clone;
}

function isLocaleMessageGroup(value: LocaleMessage | undefined): value is LocaleMessages {
  return typeof value === 'object' && value !== null;
}
